import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import FormControl from '@mui/material/FormControl';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import TextField from '@mui/material/TextField';

import FormHelperText from '@mui/material/FormHelperText';
import Collapse from '@mui/material/Collapse';
// eslint-disable-next-line import/no-unresolved
import { ErrorMessage } from '@hookform/error-message';
import { Controller } from 'react-hook-form';
import { FormLabel } from '@mui/material';

const DatePickerComponent = ({
    name,
    label,
    value,
    placeholder,
    format,
    onChange,
    helperText,
    errors,
    showError,
    required,
    initialFocusedDate,
    inputRef,
    inputProps,
    hiddenLabel,
    showRequired,
    disabled,
    disablePast,
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, [setIsFocused]);

    const handleBlur = useCallback(() => {
        setIsFocused(false);
    }, [setIsFocused]);

    const isInvalid = !!errors[name];
    let describedBy = helperText ? `${name}-helper` : null;
    if (showError && isInvalid) {
        describedBy = describedBy ? `${describedBy} ${name}-error` : `${name}-error`;
    }

    const elementProps = {
        ...inputProps,
        'aria-labelledby': `${name}-label`,
        'aria-describedby': describedBy,
        'aria-invalid': isInvalid,
        'aria-required': required,
        onFocus: handleFocus,
        onBlur: handleBlur,
    };

    return (
        <FormControl component="fieldset" error={isInvalid} fullWidth required={required}>
            {!hiddenLabel && (
                <FormLabel
                    htmlFor={name}
                    required={showRequired && required}
                    disabled={disabled}
                    error={isInvalid}
                    style={{ fontWeight: 600 }}
                >
                    {label}
                </FormLabel>
            )}

            <DesktopDatePicker
                disabled={disabled}
                disablePast={disablePast}
                name={name}
                value={value}
                inputFormat={format}
                placeholder={placeholder}
                onChange={(date) => onChange(date, name)}
                inputRef={inputRef}
                required={required}
                error={isInvalid}
                initialFocusedDate={initialFocusedDate}
                inputProps={elementProps}
                OpenPickerButtonProps={
                    {
                        // type: '', // default value is "button" which overrides MUI styles
                        // adjusting the type to '' appears to trigger premature browser validation
                    }
                }
                KeyboardButtonProps={{
                    'aria-label': 'change date',
                }}
                rightArrowButtonProps={{
                    'aria-label': 'next month',
                }}
                leftArrowButtonProps={{
                    'aria-label': 'previous month',
                }}
            />

            {helperText && (
                <Collapse in={isFocused}>
                    <FormHelperText id={`${name}-helper`}>{helperText}</FormHelperText>
                </Collapse>
            )}

            {showError && (
                <FormHelperText id={`${name}-error`} role="alert" aria-live="polite">
                    <ErrorMessage name={name} errors={errors} />
                </FormHelperText>
            )}
        </FormControl>
    );
};

DatePickerComponent.defaultProps = {
    value: null,
    format: 'MM/dd/yyyy',
    placeholder: null,
    errors: {},
    showError: true,
    helperText: null,
    required: false,
    initialFocusedDate: null,
    inputRef: null,
    inputProps: {},
    hiddenLabel: false,
    showRequired: false,
    disabled: false,
    disablePast: false,
};

DatePickerComponent.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    format: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    helperText: PropTypes.string,
    errors: PropTypes.object,
    showError: PropTypes.bool,
    required: PropTypes.bool,
    initialFocusedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    inputProps: PropTypes.object,
    hiddenLabel: PropTypes.bool,
    showRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    disablePast: PropTypes.bool,
};

const DatePickerWrapper = React.forwardRef(({ control, rules, inputRef, ...pickerProps }, ref) => {
    if (control) {
        return (
            <Controller
                control={control}
                rules={rules}
                name={pickerProps.name}
                render={({ field: { onChange, onBlur, value } }) => (
                    <DesktopDatePicker
                        onChange={onChange}
                        onBlur={onBlur}
                        selected={value}
                        renderInput={(params) => (
                            <TextField
                                required={pickerProps.showRequired && pickerProps.required}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                {...params}
                                label="Enter Date"
                            />
                        )}
                        {...pickerProps}
                    />
                )}
            />
        );
    }
    return <DatePickerComponent inputRef={ref} {...pickerProps} />;
});

DatePickerWrapper.defaultProps = {
    value: null,
    format: 'MM/dd/yyyy',
    placeholder: null,
    onChange: null,
    helperText: null,
    errors: {},
    showError: true,
    required: false,
    initialFocusedDate: null,
    inputRef: null,
    inputProps: {},
    control: null,
    rules: null,
};

DatePickerWrapper.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    format: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func, // required if control not provided
    helperText: PropTypes.string,
    errors: PropTypes.object,
    showError: PropTypes.bool,
    required: PropTypes.bool,
    initialFocusedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    inputProps: PropTypes.object,
    control: PropTypes.object,
    rules: PropTypes.object,
};

export default DatePickerWrapper;
